package com.exura.features.plandefinition.data.repository

import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.element.*
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.plan.PreparationPlan
import com.exura.features.plandefinition.domain.model.plan.TreatmentPlan
import com.exura.features.plandefinition.domain.model.series.*
import com.exura.features.plandefinition.domain.repository.*
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.DocumentReference
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first


class PlanDefinitionRepositoryImpl(
    val planRepository: BasePlanRepository,
    val collectionRepository: PlanCollectionRepository,
    val seriesRepository: PlanSeriesRepository,
    val elementsRepository: PlanElementsRepository,
) : PlanDefinitionRepository {


    companion object {
        private const val COLLECTIONS_SUBCOLLECTION = "collections"
        private const val SERIES_SUBCOLLECTION = "series"
        private const val ELEMENTS_SUBCOLLECTION = "elements"
        private const val DIRECT_SERIES_SUBCOLLECTION = "directSeries"
    }

    //TODO: Maybe should also abstract away having to specify full references? the saving prefix depends on user type,
    // so maybe add something like collection prefix and reroute these methods to always do that?
    // then the repos would be just created with prefix taken from user type

    override suspend fun read(source: DocumentReference): Result<BasePlan> = runCatching {
        // 1. Load the base plan
        val plan = planRepository.read(source).getOrThrow()

        val collections = readCollections(plan)
        val directSeries = readSeries(plan, null)

        when (plan) {
            is PreparationPlan -> plan.copy(collections = collections, directSeries = directSeries)
            is TreatmentPlan -> plan.copy(collections = collections, directSeries = directSeries)
            else -> throw IllegalArgumentException("")
        }
    }

    override suspend fun create(item: BasePlan, dest: CollectionReference): Result<BasePlan> = runCatching {
        val plan = planRepository.create(item, dest).getOrThrow()
        val collections = saveCollections(plan, item.collections)
        val directSeries = saveSeries(plan, null, item.directSeries)
        when (plan) {
            is PreparationPlan -> plan.copy(collections = collections, directSeries = directSeries)
            is TreatmentPlan -> plan.copy(collections = collections, directSeries = directSeries)
            else -> throw IllegalArgumentException("")
        }
    }

    override suspend fun update(item: BasePlan, dest: DocumentReference): Result<BasePlan> = runCatching {
        val plan = planRepository.update(item, dest).getOrThrow()
        val collections = saveCollections(item)
        val directSeries = saveSeries(item)

        when (plan) {
            is PreparationPlan -> plan.copy(collections = collections, directSeries = directSeries)
            is TreatmentPlan -> plan.copy(collections = collections, directSeries = directSeries)
            else -> throw IllegalArgumentException("")
        }
    }

    private suspend fun readCollections(plan: BasePlan): List<Collection> {
        // Get all collections
        val collections = getCollections(plan).first()

        // For each collection, load its series with elements
        return collections.map { collection ->
            // Read all series for this collection
            val series = readSeries(plan, collection.firebaseDocId)

            // Create a new collection with the loaded series
            collection.copy(series = series)
        }
    }

    private suspend fun readSeries(plan: BasePlan, collectionId: String?): List<BaseSeries<out BasePlanElement>> {
        // Get all series for this collection or directly from the plan
        val series = getAllSeries(plan, collectionId).first()

        // For each series, load its elements
        return series.map { currentSeries ->
            // Read all elements for this series
            val elements = readElements(plan, collectionId, currentSeries.firebaseDocId!!)

            // Create a new series with the loaded elements
            createSeriesWithElements(currentSeries, elements)
        }
    }

    private suspend fun readElements(plan: BasePlan, collectionId: String?, seriesId: String): List<BasePlanElement> {
        return getElements(plan, collectionId, seriesId).first()
    }

    /**
     * Creates a copy of the series with its elements properly typed and attached.
     *
     * @param series The series to copy
     * @param elements The elements to attach to the series
     * @return A new series with the elements attached
     */
    private fun createSeriesWithElements(
        series: BaseSeries<*>,
        elements: List<BasePlanElement>
    ): BaseSeries<out BasePlanElement> {
        return when (series) {
            is SimpleInstructionSeries -> {
                val typedElements = elements.filterIsInstance<InstructionElement>()
                series.copy(elements = typedElements)
            }

            is SimplePersistentSeries -> {
                val typedElements = elements.filterIsInstance<PersistentElement>()
                series.copy(elements = typedElements)
            }

            is SimpleQuestionSeries -> {
                val typedElements = elements.filterIsInstance<QuestionElement>()
                series.copy(elements = typedElements)
            }

            is SimpleFloatingSeries -> {
                val typedElements = elements.filterIsInstance<FloatingElement>()
                series.copy(elements = typedElements)
            }

            else -> throw IllegalArgumentException("")
        }
    }

    override suspend fun delete(dest: DocumentReference): Result<Boolean> = planRepository.delete(dest)

    override suspend fun set(item: BasePlan, dest: DocumentReference): Result<BasePlan> = planRepository.set(item, dest)

    override suspend fun list(dest: CollectionReference): Flow<List<BasePlan>> = planRepository.list(dest)


    private suspend fun saveCollections(plan: BasePlan): List<Collection> = saveCollections(plan, plan.collections)

    private suspend fun saveSeries(plan: BasePlan, collectionId: String?): List<BaseSeries<out BasePlanElement>> =
        saveSeries(plan, collectionId, plan.directSeries)

    private suspend fun saveSeries(plan: BasePlan): List<BaseSeries<out BasePlanElement>> =
        saveSeries(plan, null, plan.directSeries)


    // Collection Management
    private fun getCollectionsCollectionRef(plan: BasePlan): CollectionReference {
        val planDocRef = plan.firebaseDocPrefix!!.document(plan.firebaseDocId!!)
        return planDocRef.collection(COLLECTIONS_SUBCOLLECTION)
    }

    private fun getSeriesCollectionRef(plan: BasePlan, collectionId: String?): CollectionReference {
        val planDocRef = plan.firebaseDocPrefix!!.document(plan.firebaseDocId!!)
        return if (collectionId != null) {
            planDocRef.collection(COLLECTIONS_SUBCOLLECTION).document(collectionId).collection(SERIES_SUBCOLLECTION)
        } else {
            planDocRef.collection(DIRECT_SERIES_SUBCOLLECTION)
        }
    }

    private fun getElementsCollectionRef(plan: BasePlan, collectionId: String?, seriesId: String): CollectionReference {
        val seriesRef = getSeriesCollectionRef(plan, collectionId).document(seriesId)
        return seriesRef.collection(ELEMENTS_SUBCOLLECTION)
    }

    override suspend fun getCollection(plan: BasePlan, collectionId: String): Result<Collection> {
        val collectionsRef = getCollectionsCollectionRef(plan)
        return collectionRepository.read(collectionsRef.document(collectionId))
    }

    override suspend fun getCollections(plan: BasePlan): Flow<List<Collection>> {
        val collectionsRef = getCollectionsCollectionRef(plan)
        return collectionRepository.list(collectionsRef)
    }

    // Series Methods
    override suspend fun getSeries(
        plan: BasePlan,
        seriesCollectionId: String?,
        seriesId: String
    ): Result<BaseSeries<out BasePlanElement>> {
        val seriesCollection = getSeriesCollectionRef(plan, seriesCollectionId)
        return seriesRepository.read(seriesCollection.document(seriesId))
    }

    override suspend fun getAllSeries(
        plan: BasePlan,
        seriesCollectionId: String?
    ): Flow<List<BaseSeries<out BasePlanElement>>> {
        val seriesCollection = getSeriesCollectionRef(plan, seriesCollectionId)
        return seriesRepository.list(seriesCollection)
    }

    // Element Methods
    override suspend fun getElement(
        plan: BasePlan,
        seriesCollectionId: String?,
        seriesId: String,
        elementId: String
    ): Result<BasePlanElement> {
        val elementsCollection = getElementsCollectionRef(plan, seriesCollectionId, seriesId)
        return elementsRepository.read(elementsCollection.document(elementId))
    }

    override suspend fun getElements(
        plan: BasePlan,
        seriesCollectionId: String?,
        seriesId: String
    ): Flow<List<BasePlanElement>> {
        val elementsCollection = getElementsCollectionRef(plan, seriesCollectionId, seriesId)
        return elementsRepository.list(elementsCollection)
    }

    // Saving Methods
    private suspend fun saveCollections(plan: BasePlan, collections: List<Collection>): List<Collection> {
        val collectionsRef = getCollectionsCollectionRef(plan)

        // Save each collection and its series
        return collections.map { collection ->
            val savedCollection = collectionRepository.save(collection, dest = collectionsRef).getOrThrow()
            // Save the series for this collection
            val seriesList = saveSeries(plan, savedCollection.firebaseDocId, collection.series)

            // Return collection with saved series
            savedCollection.copy(series = seriesList)
        }
    }

    private suspend fun saveSeries(
        plan: BasePlan,
        collectionId: String?,
        series: List<BaseSeries<out BasePlanElement>>
    ): List<BaseSeries<out BasePlanElement>> {
        val seriesCollection = getSeriesCollectionRef(plan, collectionId)

        // Save each series and its elements
        return series.map { currentSeries ->
            val savedSeries = seriesRepository.save(currentSeries, dest = seriesCollection).getOrThrow()

            // Save the elements for this series
            saveElements(plan, collectionId, savedSeries.firebaseDocId!!, currentSeries.elements)

            // Return the saved series
            savedSeries
        }
    }

    private suspend fun saveElements(
        plan: BasePlan,
        collectionId: String?,
        seriesId: String,
        elements: List<BasePlanElement>
    ): List<BasePlanElement> {
        val elementsCollection = getElementsCollectionRef(plan, collectionId, seriesId)

        // Save each element
        return elements.map { element ->
            elementsRepository.save(element, elementsCollection).getOrThrow()
        }
    }

}
